import { cn } from "@workspace/ui/lib/utils";
import { type HTMLMotionProps, motion } from "framer-motion";
import { forwardRef, type ReactNode, useId } from "react";

type LiquidGlassButtonColor = "sage" | "blush" | "primary" | "neutral";

type LiquidGlassButtonProps = Omit<
  HTMLMotionProps<"button">,
  "ref" | "children"
> & {
  children: ReactNode;
  color?: LiquidGlassButtonColor;
  size?: "default" | "sm" | "lg";
  fullWidth?: boolean;
};

const sizeStyles = {
  default: "h-12 px-5 py-2.5 text-base",
  sm: "h-9 px-4 py-2 text-sm",
  lg: "h-14 px-6 py-3 text-lg",
};

const colorClassMap: Record<LiquidGlassButtonColor, string> = {
  sage: "liquid-glass-btn-sage",
  blush: "liquid-glass-btn-blush",
  primary: "liquid-glass-btn-primary",
  neutral: "liquid-glass-btn-neutral",
};

export const LiquidGlassButton = forwardRef<
  HTMLButtonElement,
  LiquidGlassButtonProps
>(
  (
    {
      children,
      className,
      color = "primary",
      size = "default",
      fullWidth = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const filterId = useId();

    return (
      <motion.button
        className={cn(
          "liquid-glass-btn relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-xl font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          sizeStyles[size],
          colorClassMap[color],
          fullWidth && "w-full",
          className
        )}
        disabled={disabled}
        ref={ref}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        {...props}
      >
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          height="100%"
          width="100%"
        >
          <defs>
            <filter
              colorInterpolationFilters="sRGB"
              height="130%"
              id={filterId}
              width="130%"
              x="-15%"
              y="-15%"
            >
              <feGaussianBlur
                in="SourceGraphic"
                result="blur"
                stdDeviation="2"
              />
              <feColorMatrix
                in="blur"
                result="saturated"
                type="saturate"
                values="1.4"
              />
            </filter>
          </defs>
        </svg>

        <div className="liquid-glass-btn-backdrop absolute inset-0 rounded-xl transition-all duration-200" />

        <div
          className="pointer-events-none absolute inset-0 rounded-xl"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255, 255, 255, 0.2) 0%, transparent 60%)",
            mixBlendMode: "overlay",
          }}
        />

        <span className="relative z-10 flex items-center justify-center gap-2">
          {children}
        </span>
      </motion.button>
    );
  }
);

LiquidGlassButton.displayName = "LiquidGlassButton";
